/**
 * Tenant-binding change detection for the Apollo layer.
 *
 * The active tenant travels as the `x-syncanix-tenant` HEADER, not as a query
 * variable — so Apollo's cache cannot distinguish data fetched under one
 * tenant from another. Whenever the bound workspace id actually CHANGES, every
 * cached/active query may hold the previous tenant's data and must be purged +
 * refetched (`apolloClient.resetStore()`).
 *
 * Two real-world transitions require it (caught live 2026-06-11 — a fresh
 * browser session rendered "Capability not found" for a capability the list
 * had just linked to):
 *   1. Fresh session: queries fire under the `ws-personal` bootstrap seed
 *      until MyTenants hydration selects the real workspace.
 *   2. The user switches workspaces mid-session.
 *
 * The first-ever binding must NOT reset — no query has fired under any other
 * tenant yet, and resetting there would abort the in-flight bootstrap queries.
 * A binding that clears to null (sign-out) is owned by the auth teardown path.
 */
export function shouldResetTenantScope(previous: string | null, next: string | null): boolean {
  return previous !== null && next !== null && previous !== next;
}

/** Server-minted workspace ids are uuids; bootstrap seeds ('ws-personal') are not. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when the bound workspace id is a real (server-minted) tenant rather
 * than the pre-hydration bootstrap seed. Data queries fired before this is
 * true would bind the server's personal-tenant fallback — the hydration gate
 * in `WorkspaceHydrator` holds the authenticated app until it flips.
 */
export function isRealWorkspaceId(id: string | null): boolean {
  return id !== null && UUID_SHAPE.test(id);
}
